import { Map, List } from 'immutable'
import _ from 'lodash'

import config from '../../../../config'
import { DELETED_GROUP_MEMBER_COUNT } from './constants'
import { BUTTONSARRAY_KEY } from './components/Ribbon/constants'
import { TABS, DEFAULT_TAB } from './components/Workspace/components/Blocks/DetrimentalEvents/constants'

import { createDataTableId, createDataTableRegex, createChartRegex } from '../helpers'

const MODULE_KEY = config.modules.portfolio.key
const DETRIMENTAL_EVENTS_KEY = config.modules.portfolio.blocks.detrimentalEvents.key

export const sessionIdSelector = (state) => state.getIn(['authorization', 'sessionId'])
export const localeSelector = (state) => state.getIn(['translate', 'locale'])
export const hasAccessRightsToPortfolio = (state) =>
    state.getIn(['authorization', 'accessRights', MODULE_KEY, 'available'])
export const accessRightsLoadedSelector = (state) =>
    state.getIn(['authorization', 'accessRights', MODULE_KEY, 'isCustomLoaded'])
export const activeGroupPortfolioId = (state) => {
    const activeTab = state.getIn(['ui', 'tabs', 'tabs']).find((tab) => tab.get('active')) || null

    return (activeTab && activeTab.get('id')) || null
}

export const getRibbonActiveBlocks = (state) => {
    const data = state.getIn(['ui', 'modules', MODULE_KEY, 'ribbon', BUTTONSARRAY_KEY, 'data'], Map())
    return data
        .filter((block) => block)
        .keySeq()
        .toList()
}

export const getRibbonActivePortfolioType = (state) => {
    const portfolioType = state.getIn(['ui', 'modules', MODULE_KEY, 'ribbon', 'portfolios', 'active'], null)
    return portfolioType
}

export const getPortfolioBlocksById = (state, id, active = true) => {
    const portfolioBlocks = state.getIn(['ui', 'modules', MODULE_KEY, 'workspace', id, 'blocks'], Map())
    const portfolioActiveBlocks = portfolioBlocks.filter((block) => block.get('active') === active)

    return portfolioActiveBlocks.keySeq().toList()
}

export const getPortfolioNameById = (state, id) => {
    const portfolioName = state.getIn(['ui', 'modules', MODULE_KEY, 'workspace', id, 'name'], null)
    return portfolioName
}

export const getToggleBlocksById = (state, id) => {
    const ribbon = getRibbonActiveBlocks(state).toSet()
    const portfolio = getPortfolioBlocksById(state, id).toSet()

    return Map({
        toActive: ribbon.subtract(portfolio).toList(),
        toInactive: portfolio.subtract(ribbon).toList(),
    })
}

export const getDataTableIds = (state, id) => {
    const allIds = state.getIn(['tables', 'dataTable']).keySeq().toList()
    const regex = createDataTableRegex(MODULE_KEY, id)

    return allIds.filter((tableId) => regex.test(tableId))
}

export const getBarNestedCategoriesChartIds = (state, id) => {
    const allIds = state.getIn(['charts', 'barNestedCategories']).keySeq().toList()
    const regex = createChartRegex(MODULE_KEY, id)

    return allIds.filter((chartId) => regex.test(chartId))
}

export const getPortfolioDataTablesIds = (state, id, active = true) => {
    const portfolioBlocks = state.getIn(['ui', 'modules', MODULE_KEY, 'workspace', id, 'blocks'], Map())

    const detrimActiveTab = state.getIn(
        ['ui', 'workspace', MODULE_KEY, id, 'blocks', DETRIMENTAL_EVENTS_KEY, 'customData', 'active'],
        DEFAULT_TAB
    )
    const detrimInactiveTabs = _.filter(TABS, (tab) => tab !== detrimActiveTab)

    const ids = portfolioBlocks.reduce((acc, block, blockKey) => {
        // special behavior for DetrimentalEvents block
        if (blockKey === DETRIMENTAL_EVENTS_KEY) {
            // if we want inactive data tables
            if (!active) {
                // and detrimentalEvents block is inactive, return all his tabs data tables ids
                if (!block.get('active'))
                    return [...acc, ..._.map(TABS, (tab) => createDataTableId(MODULE_KEY, id, `${blockKey}_${tab}`))]
                // and detrimentalEvents block is active, return all his inactive tabs data tables ids
                return [
                    ...acc,
                    ..._.map(detrimInactiveTabs, (tab) => createDataTableId(MODULE_KEY, id, `${blockKey}_${tab}`)),
                ]
            }

            // if we want active data tables and detrimentalEvents block is active, return his active tab data table id
            if (block.get('active'))
                return [...acc, createDataTableId(MODULE_KEY, id, `${blockKey}_${detrimActiveTab}`)]

            // else return nothing
            return acc
        }

        // for other blocks return data table id by active param
        return block.get('active') === active ? [...acc, createDataTableId(MODULE_KEY, id, blockKey)] : acc
    }, [])

    return new List(ids)
}

export const isPortfolioOpen = (state, id) => !!state.getIn(['ui', 'modules', MODULE_KEY, 'workspace', id], null)

export const areAllBlockLoaded = (state, id) => {
    const portfolioBlocks = state.getIn(['ui', 'modules', MODULE_KEY, 'workspace', id, 'blocks'], Map())

    // if there is any not loaded block return false, else return true
    return portfolioBlocks.every((block) => block.get('isLoaded'))
}

export const getBlockLevels = (state, id, blocks) => {
    const activePortfolioType = getRibbonActivePortfolioType(state)
    const accessRightsPortfolio = state.getIn(['authorization', 'accessRights', MODULE_KEY, 'custom'], Map())
    const accessRightsBlocks = accessRightsPortfolio.getIn(['portfolios', activePortfolioType, 'blocks'], Map())

    return blocks.reduce((acc, blockKey) => {
        const defaultLevel = accessRightsBlocks.getIn([blockKey, 'defaultLevel'])
        const blockLevel = state.getIn(['ui', 'workspace', MODULE_KEY, id, 'blocks', blockKey, 'level'], defaultLevel)

        return acc.set(blockKey, blockLevel)
    }, Map())
}

export const getCountOfMembers = (state, id) => {
    const group = state
        .getIn(['ui', 'entityStack', 'groups', 'groups'])
        .find((g) => _.toString(g.get('id')) === _.toString(id))
    const membersCount = group && group.get('members')

    return _.isFinite(membersCount) ? membersCount : DELETED_GROUP_MEMBER_COUNT
}

export const getAccessRightsPortfolios = (state) =>
    state.getIn(['authorization', 'accessRights', config.modules.report.key, 'custom', 'portfolios'], Map())
